import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Check, Gift } from "lucide-react";
import { useSilver } from "@/context/SilverContext";
import { CampaignBuilder } from "@/components/silver/builder/CampaignBuilder";
import { SampleFan } from "@/components/silver/builder/SampleFan";
import { fmtInt } from "@/components/silver/common/AnimatedNumber";
import { areasText } from "@/components/silver/results/AudienceHeadline";
import { cn } from "@/lib/utils";

const EASE = [0.22, 1, 0.36, 1];

function FlyerShowcase() {
  return (
    <motion.div key="showcase" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.2 } }} transition={{ duration: 0.5, ease: EASE }}
      className="relative overflow-hidden rounded-3xl border border-white/[0.07] bg-[radial-gradient(ellipse_at_center,rgba(150,193,31,0.14),transparent_70%)] px-6 pb-5 pt-8" data-testid="campaign-flyer-fan">
      <div className="flex items-center justify-center"><SampleFan open size="lg" /></div>
      <p className="mt-5 text-center font-ui text-[10.5px] font-semibold uppercase tracking-[0.14em] text-white/45">Our homeowner campaign flyers · free samples at the enquiry step</p>
    </motion.div>
  );
}

// At the enquiry step the showcase becomes the offer: the flyers fly in, fan out, and a tick adds a free sample pack.
function FreeSamplePanel() {
  const { freeSample, setFreeSample } = useSilver();
  const [hover, setHover] = useState(false);
  return (
    <motion.button key="offer" type="button" role="checkbox" aria-checked={freeSample} onClick={() => setFreeSample(!freeSample)} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.2 } }} transition={{ duration: 0.5, ease: EASE }}
      className={cn("relative w-full overflow-hidden rounded-3xl border px-6 pb-6 pt-8 text-left transition-colors duration-300", freeSample ? "border-[#96C11F]/70 bg-[#96C11F]/10" : "border-white/[0.07] bg-white/[0.03] hover:border-white/25")}
      data-testid="free-sample-panel">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(150,193,31,0.3),transparent_65%)]" aria-hidden="true" />
      <div className="relative flex items-center justify-center"><SampleFan open size="lg" spread={freeSample || hover ? 1.25 : 1} /></div>
      <div className="relative mt-6">
        <p className="eyebrow flex items-center gap-2"><Gift className="h-3.5 w-3.5" /> Free sample pack</p>
        <h3 className="mt-1.5 text-xl font-extrabold tracking-[-0.03em] text-white">See and feel the print before you commit.</h3>
        <p className="mt-1.5 text-[13px] leading-relaxed text-white/60">We'll post you these flyers so you can check the stock, finish and feel for yourself.</p>
      </div>
      <div className={cn("relative mt-5 flex items-center gap-3 rounded-2xl border px-4 py-3.5 transition-colors duration-300", freeSample ? "border-[#96C11F] bg-[#96C11F] text-[#12290F]" : "border-white/15 bg-white/[0.04] text-white")} data-testid="free-sample-tickbox">
        <span className={cn("grid h-6 w-6 shrink-0 place-items-center rounded-md border-2 transition-colors", freeSample ? "border-[#12290F] bg-[#12290F] text-[#C6EE5A]" : "border-white/50")}>
          {freeSample && <Check className="h-4 w-4" strokeWidth={3} />}
        </span>
        <span className="text-[14px] font-bold">{freeSample ? "Free samples added to your enquiry" : "Add free samples to my enquiry"}</span>
      </div>
    </motion.button>
  );
}

export function CampaignStage() {
  const { audience, audienceLabel, goBack, builderMode } = useSilver();
  const enquiring = builderMode === "enquire";
  const label = audienceLabel.replace(/\s*households?$/i, "");

  return (
    <motion.div key="campaign" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, transition: { duration: 0.25 } }} transition={{ duration: 0.7, ease: EASE }} data-testid="campaign-stage">
      <div className="grid gap-8 lg:grid-cols-12 lg:gap-10">
        <div className="self-start lg:sticky lg:top-24 lg:col-span-5">
          <AnimatePresence initial={false}>
            {!enquiring && (
              <motion.div key="intro" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0, transition: { duration: 0.3 } }} className="overflow-hidden" data-testid="campaign-intro">
                <p className="eyebrow">Campaign builder</p>
                <h1 className="mt-3 text-4xl font-extrabold leading-[1] tracking-[-0.045em] sm:text-5xl">Build your campaign.</h1>
                <p className="mt-4 max-w-md text-[15px] leading-relaxed text-white/60" data-testid="campaign-stage-context">
                  For <span className="num font-semibold text-white">{fmtInt(audience.total)}</span> <span className="text-white/85">{label}</span> households across <span className="font-semibold text-white">{areasText(audience.areas)}</span>.
                </p>
                <div className="h-8" aria-hidden="true" />
              </motion.div>
            )}
          </AnimatePresence>
          <div className="hidden lg:block">
            <AnimatePresence mode="wait" initial={false}>
              {enquiring ? <FreeSamplePanel key="offer" /> : <FlyerShowcase key="showcase" />}
            </AnimatePresence>
          </div>
        </div>

        <div className="lg:col-span-7">
          <CampaignBuilder />
          <div className="mt-5 flex justify-center lg:justify-start">
            <button type="button" className="inline-flex items-center gap-1.5 text-[13px] text-white/50 transition-colors hover:text-white" onClick={() => goBack("results")} data-testid="campaign-back-btn">
              <ArrowLeft className="h-4 w-4" /> Back to your audience
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
